import { useState } from 'react'
import { Button } from '../ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import { Input } from '../ui/Input'
import { X, Pause, ShoppingCart } from 'lucide-react'

interface SuspendSaleModalProps {
  isOpen: boolean
  onClose: () => void
  onSuspend: (note?: string) => void
  cartItemCount: number
  cartTotal: number
  formatCurrency: (amount: number) => string
}

export const SuspendSaleModal = ({
  isOpen,
  onClose,
  onSuspend,
  cartItemCount,
  cartTotal,
  formatCurrency
}: SuspendSaleModalProps) => {
  const [note, setNote] = useState('')
  const [isSuspending, setIsSuspending] = useState(false)

  if (!isOpen) return null

  const handleSuspend = async () => {
    try {
      setIsSuspending(true)
      await onSuspend(note.trim() || undefined)
      setNote('')
      onClose()
    } catch (error) {
      console.error('Failed to suspend sale:', error)
    } finally {
      setIsSuspending(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Pause className="w-5 h-5" />
              Suspend Sale
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Sale Summary */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <ShoppingCart className="w-5 h-5 text-gray-500" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  Current Sale
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {cartItemCount} items
                </p>
              </div>
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(cartTotal)}
            </div>
          </div>

          {/* Note Input */}
          <div className="space-y-2 mb-6">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Note (Optional)
            </label>
            <Input
              placeholder="Add a note to help identify this sale later..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={100}
            />
            <p className="text-xs text-gray-500">
              This will help you identify the sale when recalling it later
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSuspend}
              disabled={isSuspending}
              className="flex-1"
            >
              {isSuspending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Suspending...
                </>
              ) : (
                <>
                  <Pause className="w-4 h-4 mr-2" />
                  Suspend Sale
                </>
              )}
            </Button>
            <Button variant="outline" onClick={onClose} disabled={isSuspending}>
              Cancel
            </Button>
          </div>

          <p className="text-xs text-gray-500 mt-4 text-center">
            Suspended sales are automatically deleted after 7 days
          </p>
        </CardContent>
      </Card>
    </div>
  )
}